import { subject } from '@casl/ability';
import { Protopie } from '@lightdash/common';
import {
    ActionIcon,
    Alert,
    Badge,
    Button,
    Card,
    Group,
    Loader,
    MultiSelect,
    NumberInput,
    SegmentedControl,
    Select,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
    Tooltip,
} from '@mantine-8/core';
import { useDebouncedValue, useDisclosure } from '@mantine-8/hooks';
import {
    IconCalculator,
    IconDeviceFloppy,
    IconHelpCircle,
    IconHistory,
    IconLock,
    IconPencil,
    IconPlus,
    IconRefresh,
    IconTrash,
    IconUsers,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import MantineIcon from '../components/common/MantineIcon';
import MantineModal from '../components/common/MantineModal';
import { useProjectUuid } from '../hooks/useProjectUuid';
import useApp from '../providers/App/useApp';
import {
    useDeleteProtopieChurnConfig,
    useProtopieChurnConfig,
    useProtopieChurnConfigs,
    useProtopieChurnEvents,
    useProtopieChurnConfigVersions,
    useProtopieChurnRun,
    useRecomputeProtopieChurnScore,
    useRenameProtopieChurnConfig,
    useRestoreProtopieChurnConfigVersion,
    useSetProtopieChurnConfigVisibility,
    useUpdateProtopieChurnConfig,
} from './api';
import classes from './ProtopieFormsPage.module.css';
import ProtopieSectionTabs from './ProtopieSectionTabs';

const aggregationOptions = [
    { value: 'pct_users_with_event', label: '% users with event' },
    { value: 'event_count', label: 'Event count' },
    { value: 'event_count_per_user', label: 'Events per user' },
    { value: 'active_days', label: 'Active days' },
];

const goalUnitOptions = [
    { value: 'fraction', label: 'Fraction' },
    { value: 'count', label: 'Count' },
    { value: 'count_per_user', label: 'Count per user' },
    { value: 'days', label: 'Days' },
];

const DEFAULT_CONFIG_NAME = Protopie.DEFAULT_CHURN_SCORE_CONFIG_NAME;
const POINT_TOTAL = 100;
const POINT_TOTAL_TOLERANCE = 0.000001;

type RubricHelpLabelProps = {
    label: string;
    description: string;
};

const RubricHelpLabel = ({ label, description }: RubricHelpLabelProps) => (
    <Group gap={4} wrap="nowrap">
        <Text span inherit>
            {label}
        </Text>
        <Tooltip
            multiline
            withinPortal
            maw={420}
            position="top-start"
            label={description}
        >
            <span>
                <MantineIcon icon={IconHelpCircle} size="sm" color="gray" />
            </span>
        </Tooltip>
    </Group>
);

const rubricLabels = {
    rubric: 'A rubric is a named churn-score formula. Saving changes creates a new version so old scores can still be audited.',
    newCustomRubric:
        'Creates a private/custom copy of the current rubric using the unsaved edits on this page. Use this to test a different scoring model without changing the shared default.',
    lookbackDays:
        'The number of days of product events included in one recompute. A 90-day lookback means only matching events from the last 90 days are counted.',
    lowRiskThreshold:
        'Risk uses scorePercent = total earned points / total possible points. If scorePercent is at or above this value, the account is Low risk. Example: 0.75 means normalized score >= 75.',
    mediumRiskThreshold:
        'If scorePercent is below the Low threshold but at or above this value, the account is Medium risk. Scores below this value are High risk.',
    factor: 'One scoring rule. This label is what users see when reviewing or editing the rubric.',
    weight: 'Maximum points this factor can contribute. All factor weights must add up to 100. Earned points = min(actual / goal, 1) * weight.',
    goal: 'The target value for this factor. Reaching the goal earns the full weight; partial progress earns proportional points.',
    unit: 'How to read the goal value: Fraction is 0-1, Count is total events, Count per user is events divided by active users, Days is active days in the lookback window.',
    aggregation:
        'How the backend calculates actual before scoring: percent of users with selected events, total event count, events per user, or active days.',
    events: 'Product event names included in this factor. Selected events are combined with OR. Active days ignores this list and counts distinct event dates.',
    scoreFunction:
        'How points are awarded. Stepwise (ChurnZero parity) gives integer point buckets per factor (e.g. 51%+ = full points, 26-50% = 66%, 1-25% = 33%); buckets are generated from each factor weight on save. Linear gives proportional partial credit: min(actual / goal, 1) * weight.',
    actions:
        'Remove this factor from the rubric. After removing factors, the remaining weights still must total 100.',
    visibility:
        'Who can read this rubric and the scores computed with it. "Everyone in the project" is the default so teammates can compare rubrics; "Only me" hides it from everyone except you and project admins. Either way, only you and admins can edit it.',
};

const toFactorInput = (
    factor: Protopie.ChurnScoreFactor,
): Protopie.ChurnScoreFactorInput => ({
    factorKey: factor.factorKey,
    label: factor.label,
    maxPoints: factor.maxPoints,
    goalValue: factor.goalValue,
    goalUnit: factor.goalUnit,
    aggregation: factor.aggregation,
    eventGroup: factor.eventGroup,
    stepThresholds: factor.stepThresholds ?? null,
    windowDays: factor.windowDays ?? null,
    sortOrder: factor.sortOrder,
});

const scoreFunctionOptions = [
    { value: 'stepwise', label: 'Stepwise (ChurnZero)' },
    { value: 'linear', label: 'Linear' },
];

const visibilityOptions = [
    { value: 'public', label: 'Everyone in the project' },
    { value: 'private', label: 'Only me' },
];

// ChurnZero parity uses integer step buckets. When a config is saved as
// stepwise, regenerate every factor's bucket from its current weight +
// aggregation so the allocation always matches the weights (and passes the
// backend's stepwise validation, which requires stepThresholds).
const withDerivedBuckets = (
    factors: Protopie.ChurnScoreFactorInput[],
    scoreFunction: Protopie.ChurnScoreFunction,
): Protopie.ChurnScoreFactorInput[] =>
    scoreFunction === 'stepwise'
        ? factors.map((factor) => ({
              ...factor,
              stepThresholds: Protopie.deriveStepThresholds(
                  factor.aggregation,
                  factor.maxPoints,
                  factor.goalValue,
                  factor.factorKey,
              ),
          }))
        : factors;

const ProtopieChurnScoreRubricPage = () => {
    const projectUuid = useProjectUuid();
    const { user } = useApp();
    const [selectedConfigName, setSelectedConfigName] =
        useState(DEFAULT_CONFIG_NAME);
    const [eventSearch, setEventSearch] = useState('');
    const [debouncedEventSearch] = useDebouncedValue(eventSearch, 300);
    const configsQuery = useProtopieChurnConfigs(projectUuid);
    const configQuery = useProtopieChurnConfig(projectUuid, selectedConfigName);
    const versionsQuery = useProtopieChurnConfigVersions(
        projectUuid,
        selectedConfigName,
    );
    const eventsQuery = useProtopieChurnEvents({
        projectUuid,
        search: debouncedEventSearch,
    });
    const updateConfig = useUpdateProtopieChurnConfig(projectUuid);
    const restoreVersion = useRestoreProtopieChurnConfigVersion(projectUuid);
    const deleteConfig = useDeleteProtopieChurnConfig(projectUuid);
    const renameConfig = useRenameProtopieChurnConfig(projectUuid);
    const setVisibility = useSetProtopieChurnConfigVisibility(projectUuid);
    const recompute = useRecomputeProtopieChurnScore(projectUuid);
    const [runUuid, setRunUuid] = useState<string | undefined>();
    const runQuery = useProtopieChurnRun({ projectUuid, runUuid });

    const [deleteModalOpened, deleteModal] = useDisclosure(false);
    const [renameModalOpened, renameModal] = useDisclosure(false);
    const [renameValue, setRenameValue] = useState('');

    const [lookbackDays, setLookbackDays] = useState(90);
    const [lowThreshold, setLowThreshold] = useState(0.75);
    const [mediumThreshold, setMediumThreshold] = useState(0.5);
    const [scoreFunction, setScoreFunction] =
        useState<Protopie.ChurnScoreFunction>('stepwise');
    const [factors, setFactors] = useState<Protopie.ChurnScoreFactorInput[]>(
        [],
    );
    const [newRubricName, setNewRubricName] = useState('');
    const [newRubricVisibility, setNewRubricVisibility] =
        useState<Protopie.ChurnScoreConfigVisibility>(
            Protopie.DEFAULT_CHURN_SCORE_VISIBILITY,
        );
    const [restoreConfigUuid, setRestoreConfigUuid] = useState<string | null>(
        null,
    );

    const applyConfigToState = useCallback(
        (data: Protopie.ChurnScoreConfigWithFactors) => {
            setLookbackDays(data.config.lookbackDays);
            setLowThreshold(data.config.riskBandThresholds.low);
            setMediumThreshold(data.config.riskBandThresholds.medium);
            setScoreFunction(data.config.scoreFunction);
            setFactors(data.factors.map(toFactorInput));
        },
        [],
    );

    useEffect(() => {
        if (!configQuery.data) return;

        applyConfigToState(configQuery.data);
    }, [configQuery.data, applyConfigToState]);

    // Reset reads the already-cached server config directly. We can't rely on
    // configQuery.refetch() here: React Query's structural sharing returns the
    // same object reference when the server payload is unchanged (it always is,
    // since only local edits changed), so the effect above never re-fires and
    // the edits would persist.
    const resetToConfig = () => {
        if (configQuery.data) {
            applyConfigToState(configQuery.data);
        }
    };

    useEffect(() => {
        if (recompute.data?.runUuid) {
            setRunUuid(recompute.data.runUuid);
        }
    }, [recompute.data?.runUuid]);

    useEffect(() => {
        if (
            runQuery.data?.status !== 'completed' &&
            runQuery.data?.status !== 'failed'
        ) {
            return undefined;
        }

        const timeout = window.setTimeout(() => {
            setRunUuid(undefined);
        }, 5000);

        return () => window.clearTimeout(timeout);
    }, [runQuery.data?.status]);

    const totalPoints = useMemo(
        () =>
            factors.reduce(
                (total, factor) => total + Number(factor.maxPoints || 0),
                0,
            ),
        [factors],
    );
    const name = configQuery.data?.config.name ?? selectedConfigName;
    const totalPointsInvalid =
        Math.abs(totalPoints - POINT_TOTAL) > POINT_TOTAL_TOLERANCE;

    const configOptions = useMemo(() => {
        const configs = configsQuery.data ?? [];
        const fallback = configQuery.data?.config;
        const allConfigs = fallback
            ? [
                  ...configs,
                  // Dedupe by name, not configUuid: the Select option value is
                  // the rubric name, and right after "save as new version" the
                  // two queries can briefly hold different versions (different
                  // UUIDs) of the same rubric, which would produce duplicate
                  // option values and crash the Mantine Select.
                  ...(configs.some((config) => config.name === fallback.name)
                      ? []
                      : [fallback]),
              ]
            : configs;

        return allConfigs.map((config) => ({
            value: config.name,
            label: `${config.name} (v${config.version})`,
        }));
    }, [configQuery.data?.config, configsQuery.data]);

    const eventOptions = useMemo(() => {
        const existingEvents = factors.flatMap(
            (factor) => factor.eventGroup.events,
        );
        const eventNames = new Set<string>([
            ...(eventsQuery.data ?? []),
            ...existingEvents,
        ]);

        return [...eventNames]
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b))
            .map((eventName) => ({
                value: eventName,
                label: eventName,
            }));
    }, [eventsQuery.data, factors]);

    const versionOptions = useMemo(
        () =>
            (versionsQuery.data ?? [])
                .filter(
                    (version) =>
                        version.configUuid !==
                        configQuery.data?.config.configUuid,
                )
                .map((version) => ({
                    value: version.configUuid,
                    label: `v${version.version} (${version.status}) - ${new Date(
                        version.updatedAt,
                    ).toLocaleString()}`,
                })),
        [configQuery.data?.config.configUuid, versionsQuery.data],
    );

    useEffect(() => {
        if (
            restoreConfigUuid &&
            !versionOptions.some((option) => option.value === restoreConfigUuid)
        ) {
            setRestoreConfigUuid(null);
        }
    }, [restoreConfigUuid, versionOptions]);

    const updateFactor = (
        index: number,
        nextFactor: Protopie.ChurnScoreFactorInput,
    ) => {
        setFactors((currentFactors) =>
            currentFactors.map((factor, factorIndex) =>
                factorIndex === index ? nextFactor : factor,
            ),
        );
    };

    const addFactor = () => {
        setFactors((currentFactors) => {
            const keys = new Set(
                currentFactors.map((factor) => factor.factorKey),
            );
            let index = 1;
            while (keys.has(`custom_factor_${index}`)) {
                index += 1;
            }

            return [
                ...currentFactors,
                {
                    factorKey: `custom_factor_${index}`,
                    label: `Custom factor ${index}`,
                    maxPoints: 0,
                    goalValue: 1,
                    goalUnit: 'count',
                    aggregation: 'event_count',
                    eventGroup: {
                        operator: 'or',
                        events: [],
                    },
                    stepThresholds: null,
                    windowDays: null,
                    sortOrder:
                        Math.max(
                            0,
                            ...currentFactors.map((factor) => factor.sortOrder),
                        ) + 10,
                },
            ];
        });
    };

    const removeFactor = (index: number) => {
        setFactors((currentFactors) =>
            currentFactors
                .filter((_, factorIndex) => factorIndex !== index)
                .map((factor, factorIndex) => ({
                    ...factor,
                    sortOrder: (factorIndex + 1) * 10,
                })),
        );
    };

    const thresholdsInvalid = lowThreshold <= mediumThreshold;
    const rubricInvalid = thresholdsInvalid || totalPointsInvalid;
    const isDefaultRubric = name === DEFAULT_CONFIG_NAME;
    const canManageProject =
        (user.data?.ability?.can(
            'manage',
            subject('Project', {
                organizationUuid: user.data.organizationUuid,
                projectUuid,
            }),
        ) ||
            user.data?.ability?.can(
                'manage',
                subject('Organization', {
                    organizationUuid: user.data?.organizationUuid,
                }),
            )) ??
        false;
    // A rubric is editable by its owner and by project/org admins. Everyone
    // else can open it (when it is public) but only to read and to fork into
    // their own copy via "Save edits as custom rubric".
    const isOwnRubric =
        !!configQuery.data?.config.createdByUserUuid &&
        configQuery.data.config.createdByUserUuid === user.data?.userUuid;
    const rubricReadOnly = isDefaultRubric
        ? !canManageProject
        : !isOwnRubric && !canManageProject;
    const visibility = configQuery.data?.config.visibility ?? 'public';
    const trimmedNewRubricName = newRubricName.trim();
    const visibleRubricNameExists = (configsQuery.data ?? []).some(
        (config) =>
            config.name.toLowerCase() === trimmedNewRubricName.toLowerCase(),
    );
    const canCreateRubric =
        trimmedNewRubricName.length > 0 &&
        trimmedNewRubricName !== DEFAULT_CONFIG_NAME &&
        !visibleRubricNameExists;

    const canManageRubric = !isDefaultRubric && !rubricReadOnly;
    const trimmedRenameValue = renameValue.trim();
    const renameConflict = (configsQuery.data ?? []).some(
        (config) =>
            config.name.toLowerCase() === trimmedRenameValue.toLowerCase() &&
            config.name !== name,
    );
    const canRename =
        trimmedRenameValue.length > 0 &&
        trimmedRenameValue !== DEFAULT_CONFIG_NAME &&
        trimmedRenameValue !== name &&
        !renameConflict;

    const openRenameModal = () => {
        setRenameValue(name);
        renameModal.open();
    };

    const handleDelete = () => {
        if (!canManageRubric) {
            return;
        }

        deleteConfig.mutate(
            { name },
            {
                onSuccess: () => {
                    setSelectedConfigName(DEFAULT_CONFIG_NAME);
                    setRestoreConfigUuid(null);
                    deleteModal.close();
                },
            },
        );
    };

    const handleRename = () => {
        if (!canManageRubric || !canRename) {
            return;
        }

        renameConfig.mutate(
            { currentName: name, newName: trimmedRenameValue },
            {
                onSuccess: (response) => {
                    setSelectedConfigName(response.config.name);
                    setRenameValue('');
                    renameModal.close();
                },
            },
        );
    };

    const handleSave = () => {
        if (rubricInvalid) {
            return;
        }

        updateConfig.mutate({
            name,
            lookbackDays,
            scoreFunction,
            riskBandThresholds: {
                low: lowThreshold,
                medium: mediumThreshold,
            },
            factors: withDerivedBuckets(factors, scoreFunction),
        });
    };

    const handleCreateRubric = () => {
        if (!canCreateRubric || rubricInvalid) {
            return;
        }

        updateConfig.mutate(
            {
                name: trimmedNewRubricName,
                lookbackDays,
                scoreFunction,
                riskBandThresholds: {
                    low: lowThreshold,
                    medium: mediumThreshold,
                },
                visibility: newRubricVisibility,
                factors: withDerivedBuckets(factors, scoreFunction),
            },
            {
                onSuccess: (response) => {
                    setSelectedConfigName(response.config.name);
                    setNewRubricName('');
                    setNewRubricVisibility(
                        Protopie.DEFAULT_CHURN_SCORE_VISIBILITY,
                    );
                },
            },
        );
    };

    if (configQuery.isLoading) {
        return (
            <Group className={classes.page}>
                <Loader size="sm" />
                <Text c="dimmed">Loading churn rubric</Text>
            </Group>
        );
    }

    if (configQuery.error) {
        return (
            <Alert color="red" title="Churn rubric could not load">
                {configQuery.error.error.message}
            </Alert>
        );
    }

    return (
        <Stack className={classes.page} gap="lg">
            <Stack className={classes.section} gap="xs">
                <Group justify="space-between" align="flex-start">
                    <Stack gap={4}>
                        <Title order={3}>Churn score rubric</Title>
                        <Text c="dimmed" size="sm">
                            Select the shared default rubric or create your own
                            rubric, then compute scores from that version.
                        </Text>
                    </Stack>
                    <Group>
                        {isDefaultRubric ? (
                            <Badge variant="outline">
                                Admin-managed default
                            </Badge>
                        ) : (
                            <Tooltip
                                withinPortal
                                maw={360}
                                multiline
                                label={
                                    visibility === 'public'
                                        ? 'Every project member can see this rubric and its scores. Only you and admins can edit it.'
                                        : 'Only you and project admins can see this rubric and its scores.'
                                }
                            >
                                <Badge
                                    variant="outline"
                                    color={
                                        visibility === 'public'
                                            ? 'green'
                                            : 'gray'
                                    }
                                    leftSection={
                                        <MantineIcon
                                            icon={
                                                visibility === 'public'
                                                    ? IconUsers
                                                    : IconLock
                                            }
                                            size="sm"
                                        />
                                    }
                                >
                                    {visibility === 'public'
                                        ? 'Shared with project'
                                        : 'Private to you'}
                                </Badge>
                            </Tooltip>
                        )}
                        <Badge variant="light">
                            v{configQuery.data?.config.version}
                        </Badge>
                        <Badge
                            variant="outline"
                            color={totalPointsInvalid ? 'red' : undefined}
                        >
                            Max points {totalPoints.toFixed(2)}
                        </Badge>
                    </Group>
                </Group>

                <ProtopieSectionTabs />
            </Stack>

            <Card withBorder className={classes.formPanel}>
                <Stack gap="md">
                    <Group grow align="flex-end">
                        <Select
                            label={
                                <RubricHelpLabel
                                    label="Rubric"
                                    description={rubricLabels.rubric}
                                />
                            }
                            allowDeselect={false}
                            data={configOptions}
                            value={selectedConfigName}
                            onChange={(value) => {
                                setSelectedConfigName(
                                    value ?? DEFAULT_CONFIG_NAME,
                                );
                                setRestoreConfigUuid(null);
                            }}
                        />
                        <TextInput
                            label={
                                <RubricHelpLabel
                                    label="New custom rubric"
                                    description={rubricLabels.newCustomRubric}
                                />
                            }
                            placeholder="Example: EMEA renewal rubric"
                            value={newRubricName}
                            error={
                                visibleRubricNameExists
                                    ? 'Name already exists'
                                    : undefined
                            }
                            onChange={(event) =>
                                setNewRubricName(event.currentTarget.value)
                            }
                        />
                        <Select
                            label={
                                <RubricHelpLabel
                                    label="Who can see it"
                                    description={rubricLabels.visibility}
                                />
                            }
                            allowDeselect={false}
                            data={visibilityOptions}
                            value={newRubricVisibility}
                            onChange={(value) =>
                                setNewRubricVisibility(
                                    (value ??
                                        Protopie.DEFAULT_CHURN_SCORE_VISIBILITY) as Protopie.ChurnScoreConfigVisibility,
                                )
                            }
                        />
                        <Button
                            variant="default"
                            disabled={!canCreateRubric || rubricInvalid}
                            loading={updateConfig.isLoading}
                            onClick={handleCreateRubric}
                        >
                            Save edits as custom rubric
                        </Button>
                    </Group>

                    {canManageRubric && (
                        <Group gap="xs">
                            <Button
                                variant="default"
                                leftSection={
                                    <MantineIcon
                                        icon={
                                            visibility === 'public'
                                                ? IconLock
                                                : IconUsers
                                        }
                                    />
                                }
                                loading={setVisibility.isLoading}
                                onClick={() =>
                                    setVisibility.mutate({
                                        name,
                                        visibility:
                                            visibility === 'public'
                                                ? 'private'
                                                : 'public',
                                    })
                                }
                            >
                                {visibility === 'public'
                                    ? 'Make private'
                                    : 'Share with project'}
                            </Button>
                            <Button
                                variant="default"
                                leftSection={<MantineIcon icon={IconPencil} />}
                                onClick={openRenameModal}
                            >
                                Rename rubric
                            </Button>
                            <Button
                                variant="outline"
                                color="red"
                                leftSection={<MantineIcon icon={IconTrash} />}
                                loading={deleteConfig.isLoading}
                                onClick={deleteModal.open}
                            >
                                Delete rubric
                            </Button>
                        </Group>
                    )}

                    <Group grow align="flex-end">
                        <NumberInput
                            label={
                                <RubricHelpLabel
                                    label="Lookback days"
                                    description={rubricLabels.lookbackDays}
                                />
                            }
                            min={1}
                            value={lookbackDays}
                            onChange={(value) =>
                                setLookbackDays(Number(value) || 90)
                            }
                        />
                        <NumberInput
                            label={
                                <RubricHelpLabel
                                    label="Low risk threshold"
                                    description={rubricLabels.lowRiskThreshold}
                                />
                            }
                            min={0}
                            max={1}
                            decimalScale={2}
                            error={
                                thresholdsInvalid
                                    ? 'Must be greater than medium'
                                    : undefined
                            }
                            value={lowThreshold}
                            onChange={(value) =>
                                setLowThreshold(Number(value) || 0)
                            }
                        />
                        <NumberInput
                            label={
                                <RubricHelpLabel
                                    label="Medium risk threshold"
                                    description={
                                        rubricLabels.mediumRiskThreshold
                                    }
                                />
                            }
                            min={0}
                            max={1}
                            decimalScale={2}
                            error={
                                thresholdsInvalid
                                    ? 'Must be lower than low'
                                    : undefined
                            }
                            value={mediumThreshold}
                            onChange={(value) =>
                                setMediumThreshold(Number(value) || 0)
                            }
                        />
                    </Group>

                    <Stack gap={4}>
                        <RubricHelpLabel
                            label="Scoring method"
                            description={rubricLabels.scoreFunction}
                        />
                        <SegmentedControl
                            w="fit-content"
                            data={scoreFunctionOptions}
                            value={scoreFunction}
                            onChange={(value) =>
                                setScoreFunction(
                                    value as Protopie.ChurnScoreFunction,
                                )
                            }
                        />
                        {scoreFunction === 'stepwise' && (
                            <Text size="xs" c="dimmed">
                                ChurnZero-style integer point buckets are
                                generated from each factor&apos;s weight on
                                save.
                            </Text>
                        )}
                    </Stack>

                    <Group justify="flex-end">
                        <Button
                            variant="default"
                            leftSection={<MantineIcon icon={IconPlus} />}
                            onClick={addFactor}
                        >
                            Add factor
                        </Button>
                    </Group>

                    <Table.ScrollContainer minWidth={1300}>
                        <Table verticalSpacing="sm">
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>
                                        <RubricHelpLabel
                                            label="Factor"
                                            description={rubricLabels.factor}
                                        />
                                    </Table.Th>
                                    <Table.Th>
                                        <RubricHelpLabel
                                            label="Weight"
                                            description={rubricLabels.weight}
                                        />
                                    </Table.Th>
                                    <Table.Th>
                                        <RubricHelpLabel
                                            label="Goal"
                                            description={rubricLabels.goal}
                                        />
                                    </Table.Th>
                                    <Table.Th>
                                        <RubricHelpLabel
                                            label="Unit"
                                            description={rubricLabels.unit}
                                        />
                                    </Table.Th>
                                    <Table.Th>
                                        <RubricHelpLabel
                                            label="Aggregation"
                                            description={
                                                rubricLabels.aggregation
                                            }
                                        />
                                    </Table.Th>
                                    <Table.Th>
                                        <RubricHelpLabel
                                            label="Events"
                                            description={rubricLabels.events}
                                        />
                                    </Table.Th>
                                    <Table.Th>
                                        <RubricHelpLabel
                                            label="Actions"
                                            description={rubricLabels.actions}
                                        />
                                    </Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {factors.map((factor, index) => (
                                    <Table.Tr key={index}>
                                        <Table.Td>
                                            <TextInput
                                                value={factor.label}
                                                onChange={(event) =>
                                                    updateFactor(index, {
                                                        ...factor,
                                                        label: event
                                                            .currentTarget
                                                            .value,
                                                    })
                                                }
                                            />
                                        </Table.Td>
                                        <Table.Td>
                                            <NumberInput
                                                min={0}
                                                value={factor.maxPoints}
                                                onChange={(value) =>
                                                    updateFactor(index, {
                                                        ...factor,
                                                        maxPoints:
                                                            Number(value) || 0,
                                                    })
                                                }
                                            />
                                            {scoreFunction === 'stepwise' && (
                                                <Tooltip
                                                    withinPortal
                                                    label="Stepwise point buckets (top→bottom): full / 66% / 33% / 0"
                                                >
                                                    <Text
                                                        size="xs"
                                                        c="dimmed"
                                                        mt={4}
                                                    >
                                                        {Protopie.deriveStepThresholds(
                                                            factor.aggregation,
                                                            factor.maxPoints,
                                                            factor.goalValue,
                                                            factor.factorKey,
                                                        )
                                                            .ranges.map(
                                                                (range) =>
                                                                    range.points,
                                                            )
                                                            .join(' · ')}
                                                    </Text>
                                                </Tooltip>
                                            )}
                                        </Table.Td>
                                        <Table.Td>
                                            <NumberInput
                                                min={0}
                                                value={factor.goalValue}
                                                onChange={(value) =>
                                                    updateFactor(index, {
                                                        ...factor,
                                                        goalValue:
                                                            Number(value) || 0,
                                                    })
                                                }
                                            />
                                        </Table.Td>
                                        <Table.Td>
                                            <Select
                                                data={goalUnitOptions}
                                                value={factor.goalUnit}
                                                onChange={(value) =>
                                                    updateFactor(index, {
                                                        ...factor,
                                                        goalUnit: (value ??
                                                            'fraction') as Protopie.ChurnScoreGoalUnit,
                                                    })
                                                }
                                            />
                                        </Table.Td>
                                        <Table.Td>
                                            <Select
                                                data={aggregationOptions}
                                                value={factor.aggregation}
                                                onChange={(value) =>
                                                    updateFactor(index, {
                                                        ...factor,
                                                        aggregation: (value ??
                                                            'pct_users_with_event') as Protopie.ChurnScoreAggregation,
                                                    })
                                                }
                                            />
                                        </Table.Td>
                                        <Table.Td>
                                            <MultiSelect
                                                searchable
                                                clearable
                                                data={eventOptions}
                                                searchValue={eventSearch}
                                                onSearchChange={setEventSearch}
                                                placeholder={
                                                    factor.aggregation ===
                                                    'active_days'
                                                        ? 'Not used'
                                                        : eventsQuery.isLoading
                                                          ? 'Loading events'
                                                          : 'Select events'
                                                }
                                                disabled={
                                                    factor.aggregation ===
                                                    'active_days'
                                                }
                                                value={factor.eventGroup.events}
                                                onChange={(events) =>
                                                    updateFactor(index, {
                                                        ...factor,
                                                        eventGroup: {
                                                            operator: 'or',
                                                            events,
                                                        },
                                                    })
                                                }
                                            />
                                        </Table.Td>
                                        <Table.Td>
                                            <ActionIcon
                                                variant="subtle"
                                                color="red"
                                                aria-label="Remove factor"
                                                disabled={factors.length <= 1}
                                                onClick={() =>
                                                    removeFactor(index)
                                                }
                                            >
                                                <MantineIcon icon={IconTrash} />
                                            </ActionIcon>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    </Table.ScrollContainer>

                    {updateConfig.error && (
                        <Alert color="red" title="Save failed">
                            {updateConfig.error.error.message}
                        </Alert>
                    )}

                    {totalPointsInvalid && (
                        <Alert color="red" title="Weights must total 100">
                            Current total is {totalPoints.toFixed(2)}.
                        </Alert>
                    )}

                    {rubricReadOnly && (
                        <Alert
                            color="blue"
                            title={
                                isDefaultRubric
                                    ? 'Default rubric is shared'
                                    : 'This rubric belongs to someone else'
                            }
                        >
                            {isDefaultRubric
                                ? 'Only project or organization admins can save new default rubric versions.'
                                : 'You can read it and try out changes here, but only its creator and project admins can save over it.'}{' '}
                            Give your edits a name under
                            <Text span fw={600}>
                                {' '}
                                New custom rubric{' '}
                            </Text>
                            to keep them as your own rubric.
                        </Alert>
                    )}

                    {runQuery.data && (
                        <Alert
                            color={
                                runQuery.data.status === 'failed'
                                    ? 'red'
                                    : 'blue'
                            }
                            title={`Run ${runQuery.data.status}`}
                        >
                            {runQuery.data.status === 'completed'
                                ? `${runQuery.data.accountsScored} accounts scored`
                                : (runQuery.data.errorMessage ??
                                  runQuery.data.runUuid)}
                        </Alert>
                    )}

                    <Stack gap="xs">
                        <Text fw={600} size="sm">
                            Version history
                        </Text>
                        <Group align="flex-end" wrap="nowrap">
                            <Select
                                style={{ flex: 1 }}
                                label="Previous version"
                                description="Restoring copies the selected rubric into a new active version."
                                placeholder={
                                    versionsQuery.isLoading
                                        ? 'Loading versions'
                                        : 'No previous versions yet'
                                }
                                disabled={
                                    versionsQuery.isLoading ||
                                    versionOptions.length === 0
                                }
                                data={versionOptions}
                                value={restoreConfigUuid}
                                onChange={setRestoreConfigUuid}
                            />
                            <Button
                                variant="default"
                                leftSection={<MantineIcon icon={IconHistory} />}
                                loading={restoreVersion.isLoading}
                                disabled={!restoreConfigUuid || rubricReadOnly}
                                onClick={() => {
                                    if (!restoreConfigUuid) return;

                                    restoreVersion.mutate(restoreConfigUuid, {
                                        onSuccess: () =>
                                            setRestoreConfigUuid(null),
                                    });
                                }}
                            >
                                Restore as new version
                            </Button>
                        </Group>
                    </Stack>

                    {restoreVersion.error && (
                        <Alert color="red" title="Restore failed">
                            {restoreVersion.error.error.message}
                        </Alert>
                    )}

                    <Group justify="flex-end">
                        <Button
                            variant="default"
                            leftSection={<MantineIcon icon={IconRefresh} />}
                            onClick={resetToConfig}
                        >
                            Reset unsaved changes
                        </Button>
                        <Button
                            variant="default"
                            leftSection={<MantineIcon icon={IconCalculator} />}
                            loading={recompute.isLoading}
                            disabled={rubricInvalid || rubricReadOnly}
                            onClick={() =>
                                recompute.mutate({
                                    name,
                                    configUuid:
                                        configQuery.data?.config.configUuid,
                                })
                            }
                        >
                            Recompute now
                        </Button>
                        <Button
                            leftSection={
                                <MantineIcon icon={IconDeviceFloppy} />
                            }
                            loading={updateConfig.isLoading}
                            disabled={rubricInvalid || rubricReadOnly}
                            onClick={handleSave}
                        >
                            Save as new version
                        </Button>
                    </Group>
                </Stack>
            </Card>

            <MantineModal
                opened={deleteModalOpened}
                onClose={deleteModal.close}
                title="Delete rubric"
                variant="delete"
                resourceType="churn score rubric"
                resourceLabel={name}
                confirmLoading={deleteConfig.isLoading}
                onConfirm={handleDelete}
            >
                <Text size="sm">
                    All versions of this rubric will be removed from the editor.
                    Existing churn scores already computed with it are kept for
                    history and stay accessible from dashboards.
                </Text>
            </MantineModal>

            <MantineModal
                opened={renameModalOpened}
                onClose={renameModal.close}
                title="Rename rubric"
                confirmLabel="Rename"
                confirmDisabled={!canRename}
                confirmLoading={renameConfig.isLoading}
                onConfirm={handleRename}
            >
                <TextInput
                    label="New rubric name"
                    placeholder="Example: EMEA renewal rubric"
                    value={renameValue}
                    error={
                        renameConflict
                            ? 'Name already exists'
                            : trimmedRenameValue === DEFAULT_CONFIG_NAME
                              ? 'Reserved name'
                              : undefined
                    }
                    onChange={(event) =>
                        setRenameValue(event.currentTarget.value)
                    }
                />
            </MantineModal>
        </Stack>
    );
};

export default ProtopieChurnScoreRubricPage;
